'use client';

import { useCallback, useRef, useState } from 'react';
import { runAddOutcome } from './add-outcome-orchestrator.js';
import { INITIAL_ADD_OUTCOME_PROGRESS_STATE } from './add-outcome-types.js';
export function useAddOutcome(deps) {
  const [progress, setProgress] = useState(INITIAL_ADD_OUTCOME_PROGRESS_STATE);
  const [isPending, setIsPending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const controllerRef = useRef(null);
  const reset = useCallback(() => {
    setProgress(INITIAL_ADD_OUTCOME_PROGRESS_STATE);
    setIsPending(false);
    setResult(null);
    setError(null);
  }, []);
  const cancel = useCallback(() => {
    if (controllerRef.current !== null) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  }, []);
  const mutate = useCallback(async (params, callbacks) => {
    if (controllerRef.current !== null) {
      controllerRef.current.abort();
    }
    const controller = new AbortController();
    controllerRef.current = controller;
    setIsPending(true);
    setResult(null);
    setError(null);
    setProgress({
      ...INITIAL_ADD_OUTCOME_PROGRESS_STATE,
      status: 'preparing'
    });
    try {
      const res = await runAddOutcome({
        connection: deps.connection,
        submitTransaction: deps.submitTransaction,
        activeWallet: deps.activeWallet,
        signingDeps: deps.signingDeps,
        turnkeyDeps: deps.turnkeyDeps,
        params,
        onProgress: setProgress,
        signal: controller.signal,
        ...(deps.logger !== undefined ? {
          logger: deps.logger
        } : {})
      });
      setResult(res);
      setIsPending(false);
      callbacks?.onSuccess?.(res);
      return res;
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error(String(err));
      setError(wrapped);
      setIsPending(false);
      callbacks?.onError?.(wrapped);
      return undefined;
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
    }
  }, [deps.connection, deps.submitTransaction, deps.activeWallet, deps.signingDeps, deps.turnkeyDeps, deps.logger]);
  return {
    progress,
    isPending,
    result,
    error,
    mutate,
    reset,
    cancel
  };
}